import React, { useState, useEffect, useCallback } from 'react'
import isEmpty from 'lodash/isEmpty'
import { NavigateFn } from '@reach/router'
import { H2, Classes, ControlGroup, Intent, Checkbox } from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import {
  IconButton,
  Confirmation,
  HiddenLocationIndicator,
  Flex,
  Toaster,
  Details,
  Icon,
  Button,
  Link,
  FilterContainer,
  MultiUsageTypeSelect,
  LogoSpinner,
  Dialog,
} from '@ui'
import api, {
  UpdateFloorInput,
  UpdateFloorAssetsVersionInput,
  CreateFloorAssetsVersionInput,
  IUsageType,
} from '@api'
import { useFileDownload } from '@hooks'
import { useUsageTypes } from '../UI/selects/UsageTypeSelect'
import { ApolloError } from 'apollo-boost'
import useFloor from './hooks/useFloor'
import FloorForm from './FloorForm'
import { get } from 'lodash'
import { legacyMode } from 'App'
import { navigateToAphrodite, fetchMeta } from '@utils/navigateToAphrodite'
import track from '@track'
import Event from '@track/Event'
import { useAppData } from '@providers'
import { meridianEnabled } from './utils'
import Permissions from '@permissions/Permissions'
import { PermissionsType, IPermissionsOnFloor } from '@permissions/types'
import { useUser } from '@hooks'
import { sqFtToSqMeters, ftToMeters } from '@utils'
import QRCodeIcon from '../UI/icons/QRCodeIcon'
import DownloadIcon from '@ui/icons/DownloadIcon'
import QrImageContent from './QRCode/QrImageContent'
import QrLabelContent from './QRCode/QrLabelContent'
import { QrCodeOptions, QrCodeFileFormat } from '@api/QRCode/download'
import css from './floorDetailsDialog.module.scss'

function getDifference(array1: IUsageType[], array2: IUsageType[]) {
  return array1.filter((object1) => {
    return !array2.some((object2) => {
      return object1.id === object2.id
    })
  })
}

type FloorDetailsDialogProps = {
  floorId: string
  navigate: NavigateFn
  reload?: () => void
  openInEdit?: boolean
}
const FloorDetailsDialog: React.FunctionComponent<FloorDetailsDialogProps> = ({
  floorId,
  navigate,
  reload,
  openInEdit = false,
}: FloorDetailsDialogProps) => {
  const { user } = useUser()
  const usageTypesList = useUsageTypes(true)

  const { floor, isLoading, update, refresh } = useFloor(floorId)
  const [dirty, setDirty] = useState(false)
  const [editMode, setEditMode] = useState(openInEdit)
  const [qrCodeMode, setQrCodeMode] = useState(false)
  const [messageMode, setMessageMode] = useState<null | string>(null)
  const [usageTypeFilter, setUsageTypeFilter] = useState<IUsageType[]>([])
  const [checkInViaQR, setCheckInViaQR] = useState(false)
  const [checkInViaQRDisabled, setCheckInViaQRDisabled] = useState(false)
  const [type, setType] = useState<string>('image')
  const [color, setColor] = useState('#000')
  const [isTransparent, setIsTransparent] = useState(false)
  const [fileFormat, setFileFormat] = useState<QrCodeFileFormat>('PNG')
  const [size, setSize] = useState<{ value: string; title: string }>({
    value: 'small',
    title: 'Small',
  })
  const [processing, setProcessing] = useState(false)
  const isUsageTypeFilterEmpty = isEmpty(usageTypeFilter)

  const disableCheckInViaQR = useCallback(() => {
    setCheckInViaQRDisabled(true)
    setCheckInViaQR(false)

    if (checkInViaQR) {
      api.floor
        .updateEnforceQrCode({
          floorId,
          enforceQrCode: false,
        })
        .catch((error) => {
          Toaster.fail({ error })
        })
    }
  }, [checkInViaQR, floorId])
  const enableCheckInViaQR = useCallback(() => {
    setCheckInViaQRDisabled(false)
  }, [])

  useEffect(() => {
    if (floor) {
      setCheckInViaQR(floor.enforceQrCode)
    }
  }, [floor, setCheckInViaQR])

  useEffect(() => {
    if (floor && usageTypesList) {
      const fetchedList = usageTypesList.filter((ut) =>
        floor.printableQrCodeUsageTypes.includes(ut.name)
      )

      const diff = [
        ...getDifference(fetchedList, usageTypesList),
        ...getDifference(usageTypesList, fetchedList),
      ]

      setUsageTypeFilter(
        diff.length === 0
          ? [{ id: 'all', name: 'all', displayName: 'All' }, ...fetchedList]
          : fetchedList
      )
    }
  }, [usageTypesList, floor])

  useEffect(() => {
    if (isUsageTypeFilterEmpty) {
      disableCheckInViaQR()
      return
    }
    enableCheckInViaQR()
  }, [enableCheckInViaQR, disableCheckInViaQR, isUsageTypeFilterEmpty])

  const {
    integrations,
    product: { features },
  } = useAppData()

  const showQRCodeSection = features.includes('qrCode') && user?.company.settings.qrCodeEnabled

  const showIntegrations = integrations && meridianEnabled(integrations.active)
  const startFileDownload = useFileDownload()

  const onClose = () => navigate && navigate(window.location.pathname)

  function commitUpdate(
    f: UpdateFloorInput,
    a?: CreateFloorAssetsVersionInput | UpdateFloorAssetsVersionInput
  ) {
    return api.building.floor.update(f).then((floor) => {
      reload?.()
      if (a) {
        if ('versionId' in a && a.versionId) {
          delete a.versionId
        }
        const promise = api.building.floor.assets.create({ ...a, floorId })
        return promise.then(() => {
          refresh().then((floor) => {
            update(floor)
            setDirty(false)
            setEditMode(false)
            Toaster.success({ message: 'Floor updated' })
          })
        })
      } else {
        update(floor)
        setDirty(false)
        setEditMode(false)
        Toaster.success({ message: 'Floor updated' })
        fetchMeta()
      }
    })
  }

  const onFileformatChange = (fileFormat: QrCodeFileFormat) => {
    if (fileFormat === 'PDF' || fileFormat === 'JPG') {
      setIsTransparent(false)
    }

    setFileFormat(fileFormat)
  }

  const onTypeChange = (type: string) => {
    if (type === 'label') {
      setIsTransparent(false)
    }

    setType(type)
  }

  const onDownload = () => {
    setProcessing(true)
    const input: QrCodeOptions = {
      spaceIds: [],
      floorIds: [floorId],
      generatorOptions: {
        fileFormat,
        size: size.value,
        color,
        type,
        is_transparent: isTransparent,
      },
    }

    api.QRCode.download(input)
      .then((message) => {
        setQrCodeMode(false)
        setMessageMode(message)
      })
      .catch((error) => Toaster.fail({ error }))
      .finally(() => setProcessing(false))
    track.downloadQRCode({
      eventName: Event.DOWNLOAD_QR_CODE,
      props: {
        component: 'Floor Details',
        actionName: 'Download QR',
      },
    })
  }

  const onCheckInViaQRChange = () => {
    setCheckInViaQR(!checkInViaQR)
    api.floor
      .updateEnforceQrCode({
        floorId,
        enforceQrCode: !checkInViaQR,
      })
      .catch((error) => {
        setCheckInViaQR(checkInViaQR)
        Toaster.fail({ error })
      })
  }

  return (
    <Permissions type={PermissionsType.FLOOR} on={floor}>
      {(permissions) => {
        const { canUpdate, canDelete, canHide } = permissions as IPermissionsOnFloor

        if (messageMode) {
          return (
            <Dialog
              isOpen
              title={<div style={{ marginLeft: '15px' }}>Download QR Codes</div>}
              style={{ width: 800 }}
              canEscapeKeyClose={false}
              canOutsideClickClose={false}
              transitionDuration={200}
              isCloseButtonShown={false}
              onClose={onClose}
              toolbar={<DialogToolbar onClose={onClose} isDirty={dirty} />}
              body={
                <div style={{ marginLeft: '20px' }}>
                  <Flex align="center" justify="center" style={{ paddingBottom: 8 }}>
                    <LogoSpinner size={30} />
                  </Flex>
                  <div style={{ margin: '25px 0px' }}>{messageMode}</div>
                  <section className={`${css.section} flex justify-end`}>
                    <Button style={{ marginLeft: 18 }} text="OK" onClick={onClose} />
                  </section>
                </div>
              }
            />
          )
        }

        if (editMode) {
          return (
            <Dialog
              isOpen
              title="Update Floor"
              style={{ width: 800 }}
              canEscapeKeyClose={false}
              canOutsideClickClose={false}
              transitionDuration={200}
              isCloseButtonShown={false}
              onClose={onClose}
              toolbar={
                isLoading || !floor ? (
                  <LoadingState onClose={onClose} showIntegrations={showIntegrations} />
                ) : (
                  <DialogToolbar onClose={onClose} isDirty={dirty} />
                )
              }
              body={
                isLoading || !floor ? (
                  <LoadingState onClose={onClose} showIntegrations={showIntegrations} />
                ) : (
                  <>
                    <FloorForm
                      dirty={dirty}
                      setDirty={setDirty}
                      floor={floor}
                      onCancel={() => {
                        setDirty(false)
                        setEditMode(false)
                      }}
                      showIntegrations={showIntegrations}
                      onClose={onClose}
                      onSubmit={(floor, assetVersion) =>
                        commitUpdate({ ...floor, id: floorId }, assetVersion)
                      }
                    />
                  </>
                )
              }
            />
          )
        }

        if (qrCodeMode) {
          return (
            <Dialog
              isOpen
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div
                    style={{ height: 56, backgroundColor: '#F1F3F4' }}
                    className="flex items-center">
                    <Button
                      minimal
                      iconName="chevron-left"
                      text="Back"
                      style={{ backgroundColor: '#F1F3F4' }}
                      onClick={() => setQrCodeMode(false)}
                    />
                    <div style={{ marginLeft: '15px' }}>Download QR Codes</div>
                  </div>
                  <DialogToolbar
                    onClose={onClose}
                    isDirty={dirty}
                    containerStyle={{ backgroundColor: '#F1F3F4' }}
                    closeButtonStyle={{ backgroundColor: '#F1F3F4' }}
                    containerClassName="big-header"
                  />
                </div>
              }
              className="gray-header"
              style={{ width: 800 }}
              canEscapeKeyClose={false}
              canOutsideClickClose={false}
              transitionDuration={200}
              isCloseButtonShown={false}
              onClose={onClose}
              body={
                isLoading || !floor ? (
                  <LoadingState onClose={onClose} showIntegrations={showIntegrations} />
                ) : (
                  <>
                    <div style={{ paddingLeft: '20px', paddingTop: '16px' }}>
                      <ControlGroup style={{ marginBottom: 8 }}>
                        <Button
                          style={{ borderRadius: '4px 0 0 4px', width: '150px', height: '32px' }}
                          onClick={() => onTypeChange('image' as string)}
                          intent={type === 'image' ? Intent.PRIMARY : Intent.NONE}
                          text="QR Code Image"
                        />
                        <Button
                          blue
                          style={{ borderRadius: '0 4px 4px 0', width: '153px', height: '32px' }}
                          onClick={() => onTypeChange('label' as string)}
                          intent={type === 'label' ? Intent.PRIMARY : Intent.NONE}
                          text="QR Code Label"
                        />
                      </ControlGroup>
                      {type === 'image' && (
                        <QrImageContent
                          size={size}
                          type={type}
                          color={color}
                          fileFormat={fileFormat}
                          onFileFormatChange={onFileformatChange}
                          isTransparent={isTransparent}
                          isProcessing={processing}
                          onSizeChange={setSize}
                          onDownload={onDownload}
                          onChangeColor={(color) => setColor(color)}
                          onTransparentChange={() => setIsTransparent((value) => !value)}
                          onCancel={() => setQrCodeMode(false)}
                        />
                      )}
                      {type === 'label' && (
                        <QrLabelContent
                          color={color}
                          type={type}
                          size={size}
                          isProcessing={processing}
                          onSizeChange={setSize}
                          onDownload={onDownload}
                          onChangeColor={setColor}
                          onCancel={() => setQrCodeMode(false)}
                          onFileFormatChange={onFileformatChange}
                        />
                      )}
                    </div>
                  </>
                )
              }
            />
          )
        }

        return (
          <Dialog
            isOpen
            style={{ width: 800 }}
            transitionDuration={200}
            canEscapeKeyClose={false}
            isCloseButtonShown={false}
            title={
              !floor || floor.id !== floorId ? (
                <LoadingState onClose={onClose} showIntegrations={showIntegrations} />
              ) : (
                <DialogToolbar
                  onEdit={
                    canUpdate
                      ? () => {
                          setEditMode(true)
                          track.buildings({
                            eventName: Event.EDIT_RECORD_DIALOG,
                            props: {
                              component: 'Floor Details',
                            },
                          })
                        }
                      : undefined
                  }
                  onDelete={
                    canDelete
                      ? () => {
                          track.buildings({
                            eventName: Event.DELETE_RECORD_DIALOG,
                            props: {
                              component: 'Floor Details',
                            },
                          })
                          return api.building.floor
                            .remove(floorId as string)
                            .then(() => {
                              if (reload) {
                                reload()
                              }
                              onClose()
                            })
                            .then(() => Toaster.success({ message: 'Floor removed' }))
                            .catch((error: ApolloError) => {
                              Toaster.fail({ error })
                            })
                        }
                      : undefined
                  }
                  navigateToMap={
                    Boolean(floor.assetVersion?.background)
                      ? () => {
                          if (floor) {
                            legacyMode
                              ? navigateToAphrodite(
                                  `floor_map&floorCode=${floor.code}&buildingCode=${floor.building.code}`
                                )
                              : navigate && navigate(`/floor_plan/${floor.id}`)
                          }
                        }
                      : undefined
                  }
                  onClose={onClose}
                  isHidden={floor.hidden}
                  toggleHide={
                    canHide
                      ? () =>
                          api.building.floor
                            .update({ id: floorId, hidden: !floor?.hidden })
                            .then((updatedFloor) => {
                              reload?.()
                              update(updatedFloor)
                              Toaster.success({
                                message: !floor?.hidden
                                  ? 'Floor marked as hidden'
                                  : 'Floor marked as visible',
                              })
                            })
                      : undefined
                  }
                />
              )
            }
            onClose={onClose}
            body={
              <div className={Classes.DIALOG_BODY}>
                {!floor || floor.id !== floorId ? (
                  <LoadingState onClose={onClose} showIntegrations={showIntegrations} />
                ) : (
                  <>
                    <Flex direction="column">
                      <Flex align="baseline">
                        <H2
                          style={{
                            marginTop: 30,
                            marginRight: 10,
                            marginBottom: 20,
                            wordBreak: 'break-word',
                          }}>
                          {floor.name}
                        </H2>
                        {floor.hidden && (
                          <div style={{ transform: 'translateY(-2px)' }}>
                            <HiddenLocationIndicator floor isVisible={!floor.hidden} />
                          </div>
                        )}
                      </Flex>
                      <Details.Columns>
                        <Details.Column>
                          <Details.Row>
                            <Details.Label label="Floor No." required />
                            {floor.story}
                          </Details.Row>
                          <Details.Row>
                            <Details.Label
                              label={`Rentable area (${user?.context.units})`}
                              required
                            />
                            {user?.context.units === 'sqm'
                              ? sqFtToSqMeters(floor.rentableArea ?? 0)
                              : floor.rentableArea ?? 0}
                          </Details.Row>
                          <Details.Row>
                            <Details.Label label="Max Capacity" required />
                            {floor.capacity}
                          </Details.Row>
                        </Details.Column>
                        <Details.Column>
                          <Details.Row>
                            <Details.Label label="Code" required />
                            {floor.code}
                          </Details.Row>
                          <Details.Row>
                            <Details.Label
                              label={`Usable area (${user?.context.units})`}
                              required
                            />
                            {user?.context.units === 'sqm'
                              ? sqFtToSqMeters(floor.usableArea ?? 0)
                              : floor.usableArea ?? 0}
                          </Details.Row>
                          <Details.Row>
                            <Details.Label label={`Area size (${user?.context.units})`} required />
                            {user?.context.units === 'sqm'
                              ? sqFtToSqMeters(floor.areaSize ?? 0)
                              : floor.areaSize ?? 0}
                          </Details.Row>
                        </Details.Column>
                      </Details.Columns>
                      <Details.Header label="Map" iconName="map" />
                      <Details.Columns>
                        <Details.Column>
                          <Details.Label label="Floor Plan Image" required />
                          <FloorImage url={floor?.assetVersion?.background?.url} />
                        </Details.Column>
                        <Details.Column>
                          <Details.Row>
                            <Details.Label label="Width (pixels)" required />
                            {get(floor, 'assetVersion.mapW', '-')}
                          </Details.Row>
                          <Details.Row>
                            <Details.Label label="Height (pixels)" required />
                            {get(floor, 'assetVersion.mapH', '-')}
                          </Details.Row>
                          <Details.Row>
                            <Details.Label
                              label={`Scale (pixels per ${
                                user?.context.units === 'sqm' ? 'meter' : 'foot'
                              })`}
                              required
                            />
                            {user?.context.units === 'sqm' ? ftToMeters(floor.scale) : floor.scale}
                          </Details.Row>
                          <Details.Row>
                            <Details.Label
                              hint={
                                <div className="centered">
                                  Using this value as radius to <br />
                                  visualize safe social distancing <br />
                                  between spaces on floor map
                                </div>
                              }
                              label={`Safe Distance (${
                                user?.context.units === 'sqm' ? 'm' : 'ft'
                              })`}
                              required
                            />
                            {user?.context.units === 'sqm'
                              ? ftToMeters(floor.safeDistance ?? 0, 1)
                              : floor.safeDistance ?? 0}
                          </Details.Row>
                          <Details.Row>
                            <Details.Label label="Floor Plan CAD" required />
                            {floor?.assetVersion?.autocad ? (
                              <Link
                                onClick={() =>
                                  floor?.assetVersion?.autocad?.url &&
                                  startFileDownload(floor.assetVersion.autocad.url)
                                }>
                                {floor.assetVersion.autocad.name}
                              </Link>
                            ) : (
                              '-'
                            )}
                          </Details.Row>
                        </Details.Column>
                      </Details.Columns>
                      {showQRCodeSection && (
                        <>
                          <Details.Header
                            iconItem={
                              <QRCodeIcon style={{ display: 'inline-block' }} color="#24b5dd" />
                            }
                            iconName=""
                            label="QR Code"
                          />
                          <Details.Row>
                            <FilterContainer style={{ background: 'unset', paddingLeft: 0 }}>
                              <MultiUsageTypeSelect
                                bookableOnly
                                values={usageTypeFilter}
                                allOptionEnable={true}
                                filterable={false}
                                skipTagIds={['all']}
                                onChange={(value) => {
                                  const backupUsageTypes = usageTypeFilter

                                  const diff = [
                                    ...getDifference(value, usageTypeFilter),
                                    ...getDifference(usageTypeFilter, value),
                                  ]

                                  if (isEmpty(value)) {
                                    disableCheckInViaQR()
                                  } else {
                                    enableCheckInViaQR()
                                  }

                                  let usageTypes: IUsageType[] = value

                                  if (diff.map((ut) => ut.name).includes('all')) {
                                    if (usageTypeFilter.map((ut) => ut.name).includes('all')) {
                                      usageTypes = []
                                    } else {
                                      usageTypes = [
                                        { id: 'all', name: 'all', displayName: 'All' },
                                        ...usageTypesList,
                                      ]
                                    }
                                  } else {
                                    if (usageTypeFilter.map((ut) => ut.name).includes('all')) {
                                      usageTypes = value.filter((v) => v.id !== 'all')
                                    } else {
                                      const diff = [
                                        ...getDifference(value, usageTypesList),
                                        ...getDifference(usageTypesList, value),
                                      ]

                                      if (diff.length === 0) {
                                        usageTypes = [
                                          { id: 'all', name: 'all', displayName: 'All' },
                                          ...usageTypesList,
                                        ]
                                      } else {
                                        usageTypes = value
                                      }
                                    }
                                  }
                                  api.building.floor
                                    .update({
                                      id: floorId,
                                      printableQrCodeUsageTypes: usageTypes
                                        .map((ut) => ut.name)
                                        .filter((ut) => ut !== 'all'),
                                    })
                                    .catch((error) => {
                                      setUsageTypeFilter(backupUsageTypes)
                                      Toaster.fail({ error })
                                    })

                                  setUsageTypeFilter(usageTypes)
                                }}
                              />
                            </FilterContainer>
                            <QRCodeButton
                              disabled={!usageTypeFilter.length}
                              onClick={() => {
                                if (usageTypeFilter.length) setQrCodeMode(true)
                              }}
                            />
                          </Details.Row>
                          <Checkbox
                            disabled={checkInViaQRDisabled}
                            checked={checkInViaQR}
                            onChange={onCheckInViaQRChange}>
                            Employees can only check in via QR code for selected desk types; manual
                            check-in via email, web or clicking on a “check-in” button will not be
                            available.
                          </Checkbox>
                        </>
                      )}
                      {showIntegrations && (
                        <>
                          <Details.Header iconName="puzzle-piece" label="Integrations Info" />
                          <Details.Row>
                            <Details.Label label="SpaceIQ Building ID" required />
                            {floor.building.id}
                          </Details.Row>
                          <Details.Row>
                            <Details.Label label="SpaceIQ Floor ID" required />
                            {floor.id}
                          </Details.Row>
                          <Details.Row>
                            <Details.Label label="Meridian Map External ID" required />
                            {get(floor, 'meridianMap.externalId') || '-'}
                          </Details.Row>
                        </>
                      )}
                    </Flex>
                  </>
                )}
              </div>
            }></Dialog>
        )
      }}
    </Permissions>
  )
}

function DialogToolbar({
  onEdit,
  onDelete,
  onClose,
  isDirty,
  isHidden,
  toggleHide,
  navigateToMap,
  closeButtonStyle,
  containerStyle,
  containerClassName,
}: {
  onEdit?: () => void
  onDelete?: () => void
  onClose?: () => void
  onCancel?: () => void
  navigateToMap?: () => void
  isDirty?: boolean
  isHidden?: boolean
  toggleHide?: () => void
  closeButtonStyle?: React.CSSProperties
  containerStyle?: React.CSSProperties
  containerClassName?: string
}) {
  return (
    <div
      style={{ display: 'flex', justifyContent: 'flex-end', ...containerStyle }}
      className={`dialog-buttons ${containerClassName ? containerClassName : ''}`}>
      <Flex align="center">
        {toggleHide && (
          <React.Fragment>
            <Tooltip2
              content={
                isHidden
                  ? 'Show floor for Viewers, IT and Booking Manager'
                  : 'Hide floor for Viewers, IT and Booking Manager'
              }>
              <Button
                minimal
                text={
                  <>
                    <span style={{ paddingRight: 10 }}>
                      <HiddenLocationIndicator floor isVisible={isHidden} />
                    </span>
                    {isHidden ? 'Show floor' : 'Hide floor'}
                  </>
                }
                onClick={toggleHide}
              />
            </Tooltip2>
            <div
              style={{
                margin: '0 10px',
                height: 24,
                borderRight: '1px solid var(--border-color)',
              }}
            />
          </React.Fragment>
        )}
        {navigateToMap && (
          <>
            <IconButton
              minimal
              iconName="map-marked"
              tooltip="Go to Floor Plan"
              onClick={() => {
                navigateToMap()
              }}
            />
            <div
              style={{
                margin: '0 10px',
                height: 24,
                borderRight: '1px solid var(--border-color)',
              }}
            />
          </>
        )}
        {onEdit && (
          <IconButton
            data-button="edit_floor"
            minimal
            iconName="pen"
            tooltip="Edit"
            onClick={onEdit}
          />
        )}
        {onDelete && (
          <Confirmation text="Delete this floor?" onConfirm={onDelete}>
            <IconButton data-button="delete_floor" minimal tooltip="Delete" iconName="trash" />
          </Confirmation>
        )}
        {onClose && isDirty ? (
          <Confirmation text="Discard unsaved changes?" onConfirm={onClose}>
            <IconButton data-button="discard_floor" minimal tooltip="Close" iconName="times" />
          </Confirmation>
        ) : (
          <IconButton
            data-button="close_dialog"
            style={closeButtonStyle}
            minimal
            tooltip="Close"
            iconName="times"
            onClick={onClose}
          />
        )}
      </Flex>
    </div>
  )
}

function LoadingState({
  onClose,
  showIntegrations,
}: {
  onClose: () => void
  showIntegrations: boolean
}) {
  return (
    <>
      <DialogToolbar onClose={onClose} />
      <Flex direction="column">
        <H2
          className={Classes.SKELETON}
          style={{
            marginTop: 30,
            marginRight: 10,
            marginBottom: 20,
            color: '#636363',
            letterSpacing: '1.27px',
          }}>
          stand in value
        </H2>
        <Details.Columns>
          <Details.Column>
            <Details.Row>
              <Details.Label label="Floor No." required />
              <span className={Classes.SKELETON}>stand in value</span>
            </Details.Row>
            <Details.Row>
              <Details.Label label="Area" required />
              <span className={Classes.SKELETON}>stand in value</span>
            </Details.Row>
          </Details.Column>
          <Details.Column>
            <Details.Row>
              <Details.Label label="Code" required />
              <span className={Classes.SKELETON}>stand in value</span>
            </Details.Row>
            <Details.Row>
              <Details.Label label="Max Capacity" required />
              <span className={Classes.SKELETON}>stand in value</span>
            </Details.Row>
          </Details.Column>
        </Details.Columns>
        <Details.Header label="Map" iconName="map" />
        <Details.Columns>
          <Details.Column>
            <Details.Label label="Floor Plan Image" required />
            <Flex
              className={Classes.SKELETON}
              direction="column"
              justify="center"
              align="center"
              style={{
                height: 150,
                color: 'var(--grey)',
                border: '1px dashed var(--grey)',
                margin: 10,
              }}>
              <Icon name="file-times" />
              <span>stand in value</span>
            </Flex>
          </Details.Column>
          <Details.Column>
            <Details.Row>
              <Details.Label label="Width (pixels)" required />
              <span className={Classes.SKELETON}>stand in value</span>
            </Details.Row>
            <Details.Row>
              <Details.Label label="Height (pixels)" required />
              <span className={Classes.SKELETON}>stand in value</span>
            </Details.Row>
            <Details.Row>
              <Details.Label label="Scale (pixels per foot)" required />
              <span className={Classes.SKELETON}>stand in value</span>
            </Details.Row>
            <Details.Row>
              <Details.Label label="Floor Plan CAD" required />
              <span className={Classes.SKELETON}>stand in value</span>
            </Details.Row>
          </Details.Column>
        </Details.Columns>
        {showIntegrations && (
          <>
            <Details.Header iconName="puzzle-piece" label="Integrations Info" />
            <Details.Columns>
              <Details.Column>
                <Details.Row>
                  <Details.Label label="SpaceIQ Building ID" required />
                  <span className={Classes.SKELETON}>stand in value</span>
                </Details.Row>
                <Details.Row>
                  <Details.Label label="Meridian Map External ID" required />
                  <span className={Classes.SKELETON}>stand in value</span>
                </Details.Row>
              </Details.Column>
              <Details.Column>
                <Details.Row>
                  <Details.Label label="SpaceIQ Floor ID" required />
                  <span className={Classes.SKELETON}>stand in value</span>
                </Details.Row>
              </Details.Column>
            </Details.Columns>
          </>
        )}
      </Flex>
    </>
  )
}

function FloorImage({ url }: { url?: string }) {
  const startFileDownload = useFileDownload()

  return url ? (
    <Tooltip2 content="Download floor plan">
      <div style={{ width: 300 }}>
        <div className={css.image_wrapper} onClick={() => url && startFileDownload(url)}>
          <div className={css.icon_wrapper}>
            <Icon name="file-download" />
          </div>
          <img alt="map" style={{ marginBottom: 5 }} width={300} height="auto" src={url} />
        </div>
      </div>
    </Tooltip2>
  ) : (
    <Flex
      direction="column"
      justify="center"
      align="center"
      style={{
        maxHeight: 150,
        color: 'var(--grey)',
        margin: 10,
      }}>
      <Icon name="file-times" />
      <span>No Map Image</span>
    </Flex>
  )
}

function QRCodeButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <div
      className={css.link_button_wrapper}
      style={{
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      onClick={onClick}>
      <DownloadIcon color="#04c8a5" /> download QR codes
    </div>
  )
}

export default FloorDetailsDialog
